import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Send, RefreshCw, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';

const parseEmailsCount = (raw) => {
  if (!raw) return 0;
  return raw
    .split(/[\s,;\n]+/)
    .map((e) => e.trim())
    .filter((e) => e.length > 0 && e.includes('@')).length;
};

const AdminPushEmailPage = () => {
  const [formData, setFormData] = useState({
    to: '',
    subject: '',
    title: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleClear = () => {
    setFormData({ to: '', subject: '', title: '', message: '' });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!formData.to.trim() || !formData.subject.trim() || !formData.title.trim()) {
      toast.error('Recipient, Subject, and Email Title are required.');
      return;
    }

    setIsLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://quivix-backend.onrender.com';
      const token = localStorage.getItem('token');
      
      const res = await axios.post(`${apiUrl}/api/admin/push-email`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const message = res.data?.message || 'Email successfully dispatched!';
      toast.success(message);
      setFormData({ to: '', subject: '', title: '', message: '' });
    } catch (err) {
      console.error('Push email failed:', err);
      const errorMsg = err.response?.data?.error || 'Failed to dispatch email.';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const recipientCount = parseEmailsCount(formData.to);

  return (
    <div className="space-y-8 animate-fade-in max-w-4xl mx-auto font-sans pb-12">
      
      {/* Header Banner */}
      <div className="admin-hero-panel flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-mono mb-2">
            <Mail className="w-3.5 h-3.5" />
            <span>Brevo SMTP Neural Link</span>
          </div>
          <h1 className="admin-heading text-3xl sm:text-4xl font-black text-white tracking-tight">
            Push Email Broadcast
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Dispatch direct HTML email broadcasts to single or multiple user recipients separated by spaces.
          </p>
        </div>

        <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2 rounded-xl shrink-0">
          <Users size={16} className="text-indigo-400" />
          <span className="text-xs font-mono text-white font-bold">{recipientCount} Recipient(s) Detected</span>
        </div>
      </div>

      {/* Main Broadcast Form */}
      <motion.div 
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-8 sm:p-10 relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 blur-[80px] rounded-full pointer-events-none" />
        
        <form onSubmit={handleSend} className="space-y-6">
          
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="admin-form-label block">Target Recipient Email(s)</label>
              <span className="text-[10px] font-mono text-slate-400">Separate multiple emails by spaces</span>
            </div>
            <div className="relative">
              <input 
                type="text" 
                name="to"
                required
                value={formData.to}
                onChange={handleChange}
                placeholder="user1@example.com user2@example.com user3@example.com"
                className="input-premium w-full pl-11 font-mono text-sm"
              />
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="admin-form-label mb-1.5 block">Email Subject</label>
              <input 
                type="text" 
                name="subject"
                required
                value={formData.subject}
                onChange={handleChange}
                placeholder="e.g. Quivix System Update & Announcement"
                className="input-premium w-full"
              />
            </div>

            <div>
              <label className="admin-form-label mb-1.5 block">Email Headline (Title)</label>
              <input 
                type="text" 
                name="title"
                required
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g. Ecosystem Update v3.0 Live"
                className="input-premium w-full"
              />
            </div>
          </div>

          <div>
            <label className="admin-form-label mb-1.5 block">Message Body Content</label>
            <textarea 
              name="message"
              rows="7"
              value={formData.message}
              onChange={handleChange}
              placeholder="Write your email broadcast text here..."
              className="input-premium w-full resize-none font-sans text-sm leading-relaxed"
            ></textarea>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-white/10">
            <button 
              type="submit" 
              disabled={isLoading}
              className="flex-1 px-6 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-900 text-white font-bold text-xs shadow-lg shadow-indigo-600/30 transition-all flex items-center justify-center gap-2 active:scale-95"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="animate-spin" size={16} />
                  <span>Dispatching Email Broadcast...</span>
                </>
              ) : (
                <>
                  <span>Dispatch Email Broadcast ({recipientCount})</span>
                  <Send size={16} />
                </>
              )}
            </button>
            <button 
              type="button"
              onClick={handleClear}
              className="px-6 py-3.5 rounded-xl bg-white/5 hover:bg-white/10 text-white font-medium text-xs transition-all"
            >
              Clear Form
            </button>
          </div>

        </form>
      </motion.div>
    </div>
  );
};

export default AdminPushEmailPage;
